import React, { useCallback, } from 'react';
import { useHistory, Link as RouterLink, } from 'react-router-dom';
import { connect, } from 'react-redux';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import MaterialLink from '@material-ui/core/Link';
import Divider from '@material-ui/core/Divider';

import Chips from 'common/components/Chips';
import { DEFAULT_GRID_CONTAINER_SPACING, } from 'common/constants';
import { useWidth, } from 'common/hooks';
import { repositories, } from 'api';
import ProductsItemAttributes from './Attributes';
import ProductsItemLabel from './Label';
import ProductsItemCover from './Cover';
import ProductsItemOrderPanel from './OrderPanel';
import ProductsItemContainer from './ItemContainer';
import { getItemsAmountInCartShop, } from 'store/ducks/shopCart/selectors';
import { ItemsInShopAmountType, ShopCartItem, } from 'store/ducks/shopCart/types';
import { Product, } from './types';
import { pages, } from 'styles';

type Props = Product & {
    images: repositories.products.CollectionTypes.ProductsImageType,
    attributes: Array<repositories.products.CollectionTypes.ProductsAttributeType>,
    labels: Array<string>,
    shortDescription: string,
    amountItemsInCartShop: ItemsInShopAmountType,
};

const mobileWiths = [ 'xs', 'sm', ];

const Link: React.FC<{ to: string, }> = ({ to, children, }) => {
    const labelClasses = pages.products.makeProductsItemLabelStyles();

    return (
        <MaterialLink component={RouterLink}
                      to={to}
                      classes={labelClasses}
                      color="inherit">
            {children}
        </MaterialLink>
    );
};

const ProductsItem: React.FC<Props> = ({
    name,
    type,
    slug,
    code,
    attributes,
    labels,
    shortDescription,
    price,
    images,
    amountItemsInCartShop,
    availability,
    minQuantity,
    taxAmount
}) => {
    const history = useHistory();
    const currentWidth = useWidth();
    const redirectionUrl = `/products/${slug}`;

    const redirectToProductDetails = useCallback(
        () => history.push(redirectionUrl),
        [ history, redirectionUrl, ]
    );

    const isMobileView = mobileWiths.includes(currentWidth);

    return (
        <ProductsItemContainer isInShopCart={amountItemsInCartShop[code] > 0}>
            <Grid container
                  spacing={DEFAULT_GRID_CONTAINER_SPACING}>
                <Grid item
                      md={8}
                      justify={isMobileView ? 'center' : undefined}
                      direction={isMobileView ? 'column' : 'row'}
                      container>
                    <Grid item
                          md={4}>
                        <Box display="flex"
                             justifyContent="center"
                             alignItems="baseline">
                            <ProductsItemCover images={images}
                                               type={type}
                                               redirectToProductDetails={redirectToProductDetails} />
                        </Box>
                    </Grid>
                    <Grid item
                          md={8}>
                        <Box textAlign={{ xs: 'center', md: 'start' }} lineHeight={1.2} marginBottom={'20px'}>
                            <Link to={redirectionUrl}>
                                {name}
                            </Link>
                        </Box>
                        <Grid item
                              container
                              alignItems={isMobileView ? 'center' : undefined}
                              direction="column">
                            <ProductsItemLabel type={type} />
                            <Box component="div"
                                 flexWrap="wrap"
                                 ml={{ xs: 2, md: 0, }}
                                 display={{ xs: 'flex', md: 'block' }}>
                                <Chips data={labels}/>
                            </Box>
                        </Grid>
                        <Box mt={1}>
                            <ProductsItemAttributes type={type}
                                                    attributes={attributes}
                                                    shortDescription={shortDescription} />
                        </Box>
                    </Grid>
                </Grid>
                <Grid item
                      md={4}
                      container>
                    <Box display="flex">
                        <Divider orientation="vertical" />
                    </Box>
                    <Grid item
                          md
                          container
                          direction="column"
                          alignItems="center"
                          justify="center">
                        <ProductsItemOrderPanel type={type}
                                                images={images}
                                                taxAmount={taxAmount}
                                                redirectToProductDetails={redirectToProductDetails}
                                                amountInCartShop={amountItemsInCartShop[code]}
                                                availability={availability}
                                                code={code}
                                                slug={slug}
                                                name={name}
                                                price={price}
                                                minQuantity={minQuantity} />
                    </Grid>
                </Grid>
            </Grid>
        </ProductsItemContainer>
    );
};

type MappingType = {
    shopCart: Array<ShopCartItem>,
};

const mapStateToProps = (state: MappingType) => ({
    amountItemsInCartShop: getItemsAmountInCartShop(state),
});

export default connect(
    mapStateToProps,
)(ProductsItem);
